import React, { useEffect } from "react";
import { ShieldCheck, FileText, Accessibility, AlertTriangle } from "lucide-react";

const SECTIONS = [
  {
    id: "privacy",
    Icon: ShieldCheck,
    title: "Privacy Policy",
    intro:
      "The Commission is committed to protecting the privacy of visitors to this website and handling personal information responsibly.",
    points: [
      "We collect only the information you choose to provide, such as your name and email when you submit an enquiry or complaint.",
      "Information submitted through our forms is used solely to respond to your request and to carry out the Commission's regulatory functions.",
      "We do not sell or rent personal information to third parties. Information may be shared with law-enforcement or other authorities where required by law.",
      "We apply reasonable administrative and technical safeguards to protect personal information against loss, misuse and unauthorised access.",
      "You may request access to, or correction of, the personal information we hold about you by contacting us through our online inquiry form.",
    ],
  },
  {
    id: "terms",
    Icon: FileText,
    title: "Terms of Use",
    intro:
      "By accessing and using this website you agree to the following terms. If you do not agree, please discontinue use of the site.",
    points: [
      "The content of this website is provided for general information about the Commission and its functions and does not constitute legal or investment advice.",
      "While we endeavour to keep information accurate and up to date, the Commission makes no warranty as to its completeness or accuracy and accepts no liability for any loss arising from reliance on it.",
      "The Regulated Entities register is published as a public service. Users should confirm a firm's status directly with the Commission before relying on it for any transaction.",
      "Unauthorised use, reproduction or distribution of the materials on this site may give rise to a claim for damages and/or be a criminal offence.",
      "These terms are governed by the laws of the Republic of Sierra Leone.",
    ],
  },
  {
    id: "accessibility",
    Icon: Accessibility,
    title: "Accessibility Statement",
    intro:
      "The Commission is committed to making this website accessible to the widest possible audience, regardless of ability or technology.",
    points: [
      "We aim to conform with recognised web accessibility guidelines, including sufficient colour contrast, keyboard navigation and descriptive text alternatives.",
      "The site is designed to be responsive and usable across desktop, tablet and mobile devices.",
      "We continually review and improve the accessibility of our content and welcome feedback from users.",
      "If you experience any difficulty accessing information on this site, please contact us through our online inquiry form and we will assist you.",
    ],
  },
  {
    id: "disclaimer",
    Icon: AlertTriangle,
    title: "Disclaimer",
    intro:
      "The information on this website, including the Regulated Entities register, is provided in good faith for public information purposes.",
    points: [
      "A status of \u201cActive\u201d indicates a firm is currently licensed; \u201cSuspended\u201d or \u201cRevoked\u201d firms are not authorised to solicit or accept investments.",
      "Inclusion in the register is not an endorsement of any firm, nor a guarantee of the safety or performance of any investment.",
      "Always exercise due diligence and seek independent professional advice before making investment decisions.",
    ],
  },
];

export default function Policies() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div>
      <section className="hero-pattern text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h1 className="font-serif text-3xl sm:text-5xl font-bold mb-4">Policies &amp; Legal</h1>
          <p className="text-slate-200 max-w-2xl text-lg">
            Our privacy, terms of use, accessibility and disclaimer policies governing the use of
            this website.
          </p>
        </div>
      </section>

      <section className="bg-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10">
          {/* Side nav */}
          <aside className="lg:col-span-3">
            <div className="lg:sticky lg:top-32 bg-slate-50 rounded-xl border border-slate-200 p-5">
              <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">
                On this page
              </h2>
              <ul className="space-y-1">
                {SECTIONS.map((s) => (
                  <li key={s.id}>
                    <a
                      href={`#${s.id}`}
                      className="flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-brand-green py-1.5"
                    >
                      <s.Icon size={16} className="text-brand-green" /> {s.title}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </aside>

          {/* Content */}
          <div className="lg:col-span-9 space-y-12">
            {SECTIONS.map((s) => (
              <article key={s.id} id={s.id} className="scroll-mt-32">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-11 h-11 rounded-lg bg-brand-navy flex items-center justify-center">
                    <s.Icon className="text-white" size={22} />
                  </div>
                  <h2 className="font-serif text-2xl font-bold text-brand-navy">{s.title}</h2>
                </div>
                <p className="text-slate-600 leading-relaxed mb-4">{s.intro}</p>
                <ul className="space-y-3">
                  {s.points.map((p, i) => (
                    <li key={i} className="flex gap-3 text-slate-700">
                      <span className="mt-2 w-1.5 h-1.5 rounded-full bg-brand-green shrink-0" />
                      <span className="leading-relaxed">{p}</span>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
            <p className="text-xs text-slate-400 border-t border-slate-200 pt-6">
              Last updated: July 2025. These policies may be revised from time to time. Continued use
              of the website constitutes acceptance of the policies in force.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
